/**
 * Casinetto Delivery Engine
 * ---------------------------------------------------------------
 * Singleton IIFE that calculates region-aware delivery promises
 * from real slot data. Consumed by: header utility strip,
 * cart drawer, cart page.
 *
 * Exposes: window.CasinettoDelivery
 * Events:  'delivery:ready' on document after init
 */
(function () {
  'use strict';

  /* ---- Constants ------------------------------------------------ */

  var DUBAI_OFFSET_MIN = 4 * 60; // UTC+4, no DST
  var LS_REGION   = 'casinetto_delivery_region';
  var LS_DISTRICT = 'casinetto_delivery_district';
  var SS_SLOTS    = 'casinetto_delivery_slots';
  var TICK_MS     = 60000;
  var TOAST_MS    = 3000;
  var URGENCY_THRESHOLD = 120; // minutes
  var LS_CUSTOMER = 'casinetto_delivery_customer';
  var SS_PROMPTED = 'casinetto_delivery_prompted';
  var SS_CART_SYNCED = 'casinetto_cart_synced';
  var PROMPT_DELAY = 3000;
  var CART_SYNC_TIMEOUT = 2000;

  var DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var DAY_FULL  = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  /* ---- Internal state ------------------------------------------- */

  var _config      = null;
  var _slotsRaw    = null;
  var _areasData   = null;
  var _callbacks   = [];
  var _promise     = null;
  var _interval    = null;
  var _toastTimer  = null;
  var _toastActive = false;
  var _loading     = true;
  var _ready       = false;
  var _cartWritePending = null;

  /* ---- Dubai time helpers --------------------------------------- */

  function dubaiNow() {
    var n = new Date();
    var utc = n.getTime() + n.getTimezoneOffset() * 60000;
    return new Date(utc + DUBAI_OFFSET_MIN * 60000);
  }

  function dubaiToday() {
    var d = dubaiNow();
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  function pad(v) { return v < 10 ? '0' + v : '' + v; }

  /** Parse "HH:MM" to minutes since midnight */
  function parseHM(s) {
    if (!s) return 0;
    var p = s.split(':');
    return (parseInt(p[0], 10) || 0) * 60 + (parseInt(p[1], 10) || 0);
  }

  /** Minutes since midnight to "9am" / "8pm" */
  function fmtTime(mins) {
    var h = Math.floor(mins / 60) % 24;
    var suffix = h >= 12 ? 'pm' : 'am';
    var h12 = h % 12 || 12;
    return h12 + suffix;
  }

  /** Positive minutes to "1h 42m" / "42m" */
  function fmtCountdown(mins) {
    if (mins <= 0) return null;
    var h = Math.floor(mins / 60);
    var m = mins % 60;
    return h > 0 ? h + 'h ' + m + 'm' : m + 'm';
  }

  /* ---- Data fetching -------------------------------------------- */

  function fetchSlots(url, cb) {
    try {
      var raw = sessionStorage.getItem(SS_SLOTS);
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached && cached.day === dubaiToday()) {
          cb(null, cached.data);
          return;
        }
      }
    } catch (_) { /* ignore */ }

    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var data;
        try {
          data = JSON.parse(text);
        } catch (_) {
          var m = text.match(/[=(]\s*(\{[\s\S]*\})\s*[;)]/);
          if (m) try { data = JSON.parse(m[1]); } catch (_2) { /* */ }
        }
        if (!data) { cb(new Error('parse'), null); return; }
        try {
          sessionStorage.setItem(SS_SLOTS, JSON.stringify({
            data: data, day: dubaiToday()
          }));
        } catch (_) { /* full */ }
        cb(null, data);
      })
      .catch(function (err) {
        try {
          var stale = sessionStorage.getItem(SS_SLOTS);
          if (stale) { cb(null, JSON.parse(stale).data); return; }
        } catch (_) { /* */ }
        cb(err, null);
      });
  }

  function fetchAreas(url, cb) {
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (txt) {
        // Shopify CDN may prepend a comment header to .js assets — strip it
        var jsonStart = txt.indexOf('{');
        if (jsonStart > 0) txt = txt.substring(jsonStart);
        // uae-areas.js has trailing commas which are invalid JSON
        var clean = txt.replace(/,\s*([\]}])/g, '$1');
        return JSON.parse(clean);
      })
      .then(function (d) { cb(null, d); })
      .catch(function (e) { cb(e, null); });
  }

  /* ---- Slot normalization --------------------------------------- */

  function normalize(raw) {
    var out = [];
    var src = raw.delivery_settings || raw.deliverytimes || raw.slots || raw;

    if (Array.isArray(src)) {
      src.forEach(function (item) {
        var itemSlots = item.slots || item.times;
        if (itemSlots && Array.isArray(itemSlots)) {
          var rn = item.region || item.region_name || item.zone || item.name || '';
          itemSlots.forEach(function (s) { out.push(norm(s, rn)); });
        } else if (item.days && typeof item.days === 'object') {
          var rn2 = item.region || item.region_name || item.zone || item.name || '';
          Object.keys(item.days).forEach(function (dk) {
            var arr = item.days[dk];
            if (!Array.isArray(arr)) return;
            arr.forEach(function (s) {
              var o = norm(s, rn2);
              o.day = dayIdx(dk);
              out.push(o);
            });
          });
        } else {
          out.push(norm(item, ''));
        }
      });
    } else if (typeof src === 'object') {
      Object.keys(src).forEach(function (rk) {
        var v = src[rk];
        (Array.isArray(v) ? v : (v && v.slots ? v.slots : [])).forEach(function (s) {
          out.push(norm(s, rk));
        });
      });
    }
    return out;
  }

  function norm(s, defRegion) {
    return {
      region:   s.region || s.region_name || s.zone || s.area || defRegion || '',
      day:      s.day != null ? dayIdx(s.day) : null,
      interval: parseInt(s.intervalDay || s.interval_day || s.interval || 0, 10) || 0,
      cutStart: s.start_cutoff || s.startCutoff || s.order_start || '00:00',
      cutEnd:   s.end_cutoff   || s.endCutoff   || s.order_end || s.cutoff || '23:59',
      delStart: s.start_time   || s.startTime   || s.delivery_start || '',
      delEnd:   s.end_time     || s.endTime     || s.delivery_end   || ''
    };
  }

  function dayIdx(k) {
    if (typeof k === 'number') return k;
    var n = parseInt(k, 10);
    if (!isNaN(n)) return n;
    var lo = ('' + k).toLowerCase();
    var i = DAY_FULL.indexOf(lo);
    if (i >= 0) return i;
    var j = DAY_SHORT.map(function (d) { return d.toLowerCase(); }).indexOf(lo);
    return j >= 0 ? j : -1;
  }

  function regionMatch(a, b) {
    if (!a || !b) return false;
    return a.trim().toLowerCase() === b.trim().toLowerCase();
  }

  function isFullDay(slot) {
    if (!slot.delStart || !slot.delEnd) return true;
    return parseHM(slot.delEnd) - parseHM(slot.delStart) >= 720;
  }

  /* ---- Promise calculation -------------------------------------- */

  function calc() {
    var region   = currentRegion();
    var district = currentDistrict();

    if (!region) {
      return mk('Set your delivery area', 'no-region', 'none', null, 'terracotta', false, null, null);
    }

    if (_loading) {
      return mk('Checking delivery times\u2026', 'loading', 'none', null, 'grey', false, district, region);
    }

    if (!_slotsRaw || _slotsRaw.length === 0) {
      return mk('Delivery available across UAE', 'unavailable', 'none', null, 'grey', false, district, region);
    }

    var now  = dubaiNow();
    var wday = now.getDay();
    var mins = now.getHours() * 60 + now.getMinutes();
    var rSlots = _slotsRaw.filter(function (s) { return regionMatch(s.region, region); });

    if (rSlots.length === 0) {
      return mk('Delivery available across UAE', 'unavailable', 'none', null, 'grey', false, district, region);
    }

    // Bookable slots: today's order-day, within cutoff window now
    var bookable = rSlots.filter(function (s) {
      if (s.day !== null && s.day !== wday) return false;
      return mins >= parseHM(s.cutStart) && mins < parseHM(s.cutEnd);
    });

    // Split by intervalDay: 0 = deliver today, 1+ = deliver later
    var sameDayBookable = bookable.filter(function (s) { return s.interval === 0; });
    var laterBookable   = bookable.filter(function (s) { return s.interval > 0; });

    // Same-day delivery available
    if (sameDayBookable.length > 0) {
      sameDayBookable.sort(function (a, b) { return parseHM(a.delStart) - parseHM(b.delStart); });
      var slot = sameDayBookable[0];
      var endT = slot.delEnd ? fmtTime(parseHM(slot.delEnd)) : '8pm';
      var left = parseHM(slot.cutEnd) - mins;
      var urgent = left > 0 && left <= URGENCY_THRESHOLD;
      var cd   = urgent ? fmtCountdown(left) : null;
      var lbl  = 'Today by ' + endT;
      return mk(lbl, 'same-day', urgent ? 'high' : 'normal', cd,
                urgent ? 'terracotta' : 'green', urgent, district, region);
    }

    // Next-day / future delivery from today's bookable slots
    if (laterBookable.length > 0) {
      laterBookable.sort(function (a, b) {
        return a.interval - b.interval || parseHM(a.delStart) - parseHM(b.delStart);
      });
      var slot2 = laterBookable[0];
      var delOffset = slot2.interval;
      var delDayIdx = (wday + delOffset) % 7;
      var dayLabel  = delOffset === 1 ? 'Tomorrow' : DAY_SHORT[delDayIdx];
      var lbl2;
      if (isFullDay(slot2)) {
        lbl2 = dayLabel + ', full day';
      } else {
        lbl2 = dayLabel + ' ' + fmtTime(parseHM(slot2.delStart)) + '\u2013' + fmtTime(parseHM(slot2.delEnd));
      }
      return mk(lbl2, delOffset === 1 ? 'next-day' : 'future', 'none', null,
                'green', false, district, region);
    }

    // No bookable slots today — check upcoming cutoffs today
    var todayUpcoming = rSlots.filter(function (s) {
      if (s.day !== null && s.day !== wday) return false;
      return mins < parseHM(s.cutStart);
    });
    if (todayUpcoming.length > 0) {
      var s3 = todayUpcoming[0];
      var ds = s3.delStart ? fmtTime(parseHM(s3.delStart)) : '';
      var upLabel = s3.interval === 0
        ? 'Today \u2014 first slot from ' + ds
        : 'Tomorrow from ' + ds;
      return mk(upLabel, 'same-day', 'normal', null,
                'green', false, district, region);
    }

    // Check tomorrow's order-day slots as last resort
    for (var off = 1; off <= 7; off++) {
      var chk = (wday + off) % 7;
      var futureSlots = rSlots.filter(function (s) { return s.day === null || s.day === chk; });
      if (futureSlots.length > 0) {
        futureSlots.sort(function (a, b) {
          return a.interval - b.interval || parseHM(a.delStart) - parseHM(b.delStart);
        });
        var s4 = futureSlots[0];
        var totalOffset = off + s4.interval;
        var delivDayIdx = (wday + totalOffset) % 7;
        var pre = totalOffset === 1 ? 'Tomorrow' : DAY_SHORT[delivDayIdx];
        var lbl3;
        if (isFullDay(s4)) {
          lbl3 = pre + ', full day';
        } else {
          lbl3 = pre + ' ' + fmtTime(parseHM(s4.delStart)) + '\u2013' + fmtTime(parseHM(s4.delEnd));
        }
        return mk(lbl3, totalOffset === 1 ? 'next-day' : 'future', 'none', null,
                  'green', false, district, region);
      }
    }

    return mk('Delivery times updating \u2014 check back soon', 'unavailable', 'none', null,
              'grey', false, district, region);
  }

  function mk(label, type, urgency, countdown, dotColor, dotPulsing, district, region) {
    return {
      label: label, type: type, urgency: urgency, countdown: countdown,
      dotColor: dotColor, dotPulsing: dotPulsing, district: district, region: region
    };
  }

  /* ---- Region getters ------------------------------------------- */

  function currentRegion() {
    try { var v = localStorage.getItem(LS_REGION); if (v) return v; } catch (_) { /* */ }
    return _config ? (_config.customerRegion || null) : null;
  }

  function currentDistrict() {
    try { var v = localStorage.getItem(LS_DISTRICT); if (v) return v; } catch (_) { /* */ }
    return _config ? (_config.customerDistrict || null) : null;
  }

  /* ---- Slot summary (for region selector) ----------------------- */

  function slotSummary(regionName) {
    if (!_slotsRaw) return '';
    var now = dubaiNow();
    var wday = now.getDay();
    var mins = now.getHours() * 60 + now.getMinutes();
    var rs = _slotsRaw.filter(function (s) { return regionMatch(s.region, regionName); });
    if (rs.length === 0) return 'next-day delivery';
    var hasSameDay = rs.some(function (s) {
      if (s.day !== null && s.day !== wday) return false;
      if (s.interval !== 0) return false;
      return mins < parseHM(s.cutEnd);
    });
    return hasSameDay ? 'same-day available' : 'next-day delivery';
  }

  /* ---- Update loop ---------------------------------------------- */

  function tick() {
    var np = calc();
    if (_promise && np.label === _promise.label &&
        np.dotColor === _promise.dotColor && np.dotPulsing === _promise.dotPulsing) {
      return;
    }
    _promise = np;
    fire();
  }

  function fire() {
    for (var i = 0; i < _callbacks.length; i++) {
      try { _callbacks[i](_promise); } catch (e) { console.error('[Delivery]', e); }
    }
  }

  /* ---- Header DOM updater --------------------------------------- */

  function updateHeader(p) {
    if (_toastActive) return;

    var el = document.querySelector('[data-delivery-indicator]');
    if (!el) return;

    var dot    = el.querySelector('[data-delivery-dot]');
    var prefix    = el.querySelector('[data-delivery-prefix]');
    var distEl    = el.querySelector('[data-delivery-district]');
    var text      = el.querySelector('[data-delivery-text]');
    var cdEl      = el.querySelector('[data-delivery-countdown]');

    if (dot) {
      dot.className = 'c-header__delivery-dot';
      dot.classList.add('c-header__delivery-dot--' + p.dotColor);
      if (p.dotPulsing) dot.classList.add('c-header__delivery-dot--pulse');
    }

    var hasDistrict = p.district && (p.type === 'same-day' || p.type === 'next-day' || p.type === 'future');
    var bar = el.closest('[data-delivery-bar]');
    if (bar) bar.classList.toggle('c-header__delivery-bar--empty', !hasDistrict);
    if (prefix) prefix.hidden = !hasDistrict;
    if (distEl) {
      distEl.hidden = !hasDistrict;
      distEl.textContent = hasDistrict ? p.district : '';
    }

    if (text) text.textContent = hasDistrict ? '\u2014 ' + p.label : p.label;
    if (cdEl) {
      cdEl.hidden = !p.countdown;
      cdEl.textContent = p.countdown ? ' \u2014 order in ' + p.countdown : '';
    }
  }

  function toast(msg) {
    var text = document.querySelector('[data-delivery-text]');
    if (!text) return;

    _toastActive = true;
    text.textContent = msg;
    clearTimeout(_toastTimer);
    _toastTimer = setTimeout(function () {
      _toastActive = false;
      if (_promise) updateHeader(_promise);
    }, TOAST_MS);
  }

  /* ---- Region Selector ------------------------------------------ */

  var _selectorOpen = false;
  var _selectorObserver = null;
  var _currentSelectorRegion = null;

  function escHtml(s) {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  function openSelector() {
    var sel = document.querySelector('[data-region-selector]');
    if (!sel) return;
    if (_selectorOpen) { closeSelector(); return; }

    sel.removeAttribute('hidden');
    // The selector renders inside the header. Where the header is hidden
    // (the mobile app's web view hides header/#header-group), the dialog
    // cannot show; locking body scroll would freeze the page on Android
    // (BMA-247). getClientRects, not offsetParent: the dialog is
    // position:fixed, so offsetParent is null even when it shows.
    if (sel.getClientRects().length === 0) {
      sel.setAttribute('hidden', '');
      return;
    }
    showStep('regions');
    populateRegions();

    requestAnimationFrame(function () {
      sel.classList.add('is-open');
      // Move focus into the dialog after animation starts
      var firstClose = sel.querySelector('[data-region-close]');
      if (firstClose) setTimeout(function () { firstClose.focus(); }, 100);
    });

    document.body.style.overflow = 'hidden';
    _selectorOpen = true;

    // The app injects its chrome-hiding CSS on load, which can land after the
    // prompt has opened: the dialog loses its box and the body stays locked.
    // A box going to 0x0 fires the observer; release the lock then.
    if (window.ResizeObserver) {
      _selectorObserver = new ResizeObserver(function () {
        if (_selectorOpen && sel.getClientRects().length === 0) closeSelector();
      });
      _selectorObserver.observe(sel);
    }
  }

  function closeSelector() {
    var sel = document.querySelector('[data-region-selector]');
    if (!sel) return;

    if (_selectorObserver) {
      _selectorObserver.disconnect();
      _selectorObserver = null;
    }

    var trigger = document.querySelector('[data-region-trigger]');

    sel.classList.remove('is-open');
    setTimeout(function () {
      sel.setAttribute('hidden', '');
      _currentSelectorRegion = null;
      // Return focus to trigger
      if (trigger) trigger.focus();
    }, 300);

    document.body.style.overflow = '';
    _selectorOpen = false;
  }


  function showStep(name) {
    var sel = document.querySelector('[data-region-selector]');
    if (!sel) return;
    var steps = sel.querySelectorAll('[data-region-step]');
    for (var i = 0; i < steps.length; i++) {
      steps[i].hidden = steps[i].dataset.regionStep !== name;
    }
  }

  function populateRegions() {
    var list = document.querySelector('[data-region-list]');
    if (!list) return;

    var regions = _areasData;
    if (!regions || !regions.length) {
      var sel = document.querySelector('[data-region-selector]');
      var loadingText = (sel && sel.dataset.i18nLoading) || 'Loading areas\u2026';
      list.innerHTML = '<div class="c-region-selector__loading">' + escHtml(loadingText) + '</div>';
      return;
    }

    var curRegion = currentRegion();
    var html = '';
    for (var i = 0; i < regions.length; i++) {
      var r = regions[i];
      var name = r.name;
      var summary = slotSummary(name);
      var isActive = regionMatch(name, curRegion);
      html += '<button class="c-region-selector__row' +
        (isActive ? ' c-region-selector__row--active' : '') +
        '" type="button" data-region-name="' + escHtml(name) + '">' +
        '<div class="c-region-selector__row-content">' +
        '<span class="c-region-selector__row-name">' + escHtml(name) + '</span>' +
        '<span class="c-region-selector__row-meta">' + escHtml(summary) + '</span>' +
        '</div>' +
        '<svg class="c-region-selector__row-chevron" width="16" height="16" viewBox="0 0 24 24" ' +
        'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="m9 6 6 6-6 6"/></svg>' +
        '</button>';
    }
    list.innerHTML = html;

    var rows = list.querySelectorAll('[data-region-name]');
    for (var j = 0; j < rows.length; j++) {
      rows[j].addEventListener('click', function () {
        selectRegionStep(this.dataset.regionName);
      });
    }
  }

  function selectRegionStep(regionName) {
    _currentSelectorRegion = regionName;
    var titleEl = document.querySelector('[data-region-district-title]');
    if (titleEl) titleEl.textContent = regionName;

    showStep('districts');
    populateDistrictList(regionName);

    var searchInput = document.querySelector('[data-region-search]');
    if (searchInput) {
      searchInput.value = '';
      setTimeout(function () { searchInput.focus(); }, 100);
    }
  }

  function populateDistrictList(regionName) {
    var list = document.querySelector('[data-region-districts]');
    if (!list) return;

    var regions = _areasData;
    var regionData = null;
    for (var i = 0; i < regions.length; i++) {
      if (regionMatch(regions[i].name, regionName)) {
        regionData = regions[i];
        break;
      }
    }

    if (!regionData || !regionData.distr) {
      list.innerHTML = '<div class="c-region-selector__empty">No districts found</div>';
      return;
    }

    renderDistricts(list, regionData.distr, regionName);
  }

  function renderDistricts(list, districts, regionName) {
    var curDistrict = currentDistrict();
    var html = '';
    for (var i = 0; i < districts.length; i++) {
      var d = districts[i].trim();
      var isActive = curDistrict && d.toLowerCase() === curDistrict.trim().toLowerCase();
      html += '<button class="c-region-selector__district' +
        (isActive ? ' c-region-selector__district--active' : '') +
        '" type="button" data-district-name="' + escHtml(d) +
        '" data-district-region="' + escHtml(regionName) + '">' +
        '<span>' + escHtml(d) + '</span>' +
        (isActive
          ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
            'stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m4 12 5 5L20 6"/></svg>'
          : '') +
        '</button>';
    }
    list.innerHTML = html;

    var rows = list.querySelectorAll('[data-district-name]');
    for (var j = 0; j < rows.length; j++) {
      rows[j].addEventListener('click', function () {
        API.setRegion(this.dataset.districtRegion, this.dataset.districtName);
        closeSelector();
      });
    }

    var active = list.querySelector('.c-region-selector__district--active');
    if (active) {
      setTimeout(function () { active.scrollIntoView({ block: 'center' }); }, 50);
    }
  }

  function showSignInNudge(sel) {
    var existing = sel.querySelector('.c-region-selector__sign-in-nudge');
    if (existing) existing.remove();

    var loginUrl = sel.dataset.loginUrl || '/account/login';
    var nudgeText = sel.dataset.i18nSignInNudge || 'Sign in to save your area across devices';

    var nudge = document.createElement('a');
    nudge.href = loginUrl;
    nudge.className = 'c-region-selector__sign-in-nudge';
    nudge.textContent = nudgeText;

    var indicator = document.querySelector('[data-delivery-indicator]');
    if (indicator && indicator.parentNode) {
      indicator.parentNode.insertBefore(nudge, indicator.nextSibling);
      // Auto-hide after 6 seconds
      setTimeout(function () { nudge.remove(); }, 6000);
    }
  }

  function bindSelectorEvents() {
    var trigger = document.querySelector('[data-region-trigger]');
    if (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        openSelector();
      });
    }

    var overlayEl = document.querySelector('[data-region-overlay]');
    if (overlayEl) {
      overlayEl.addEventListener('click', closeSelector);
    }

    var closeBtns = document.querySelectorAll('[data-region-close]');
    for (var i = 0; i < closeBtns.length; i++) {
      closeBtns[i].addEventListener('click', closeSelector);
    }

    var backBtn = document.querySelector('[data-region-back]');
    if (backBtn) {
      backBtn.addEventListener('click', function () {
        showStep('regions');
      });
    }

    var searchInput = document.querySelector('[data-region-search]');
    if (searchInput) {
      searchInput.addEventListener('input', function () {
        if (!_currentSelectorRegion) return;
        var q = this.value.toLowerCase().trim();
        var list = document.querySelector('[data-region-districts]');
        if (!list) return;

        var regions = _areasData;
        var regionData = null;
        for (var ci = 0; ci < regions.length; ci++) {
          if (regionMatch(regions[ci].name, _currentSelectorRegion)) {
            regionData = regions[ci];
            break;
          }
        }
        if (!regionData || !regionData.distr) return;

        var filtered = q
          ? regionData.distr.filter(function (d) { return d.toLowerCase().indexOf(q) >= 0; })
          : regionData.distr;

        renderDistricts(list, filtered, _currentSelectorRegion);
      });
    }

    document.addEventListener('keydown', function (e) {
      if (!_selectorOpen) return;
      if (e.key === 'Escape') {
        closeSelector();
        return;
      }
      // Focus trap
      if (e.key === 'Tab') {
        var panel = document.querySelector('[data-region-panel]');
        if (!panel) return;
        var all = panel.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])');
        var focusable = [];
        for (var fi = 0; fi < all.length; fi++) {
          if (all[fi].offsetParent !== null && !all[fi].disabled && !all[fi].hidden) {
            focusable.push(all[fi]);
          }
        }
        if (focusable.length === 0) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  /* ---- Cart attribute sync -------------------------------------- */
  // Write Region / Area cart attributes so checkout receives the
  // customer's delivery selection.  Fire-and-forget during browsing;
  // awaitCartSync() gates checkout redirect.

  function syncCartAttributes(region, district) {
    if (!region) return;
    _cartWritePending = fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { Region: region, Area: district || '', 'Delivery-Date': '', 'Delivery-Time': '', 'Start-co': '', 'End-co': '' } })
    }).catch(function (e) {
      console.warn('[Delivery] cart attribute sync failed', e);
    }).then(function () { _cartWritePending = null; });
  }

  function clearCartAttributes() {
    _cartWritePending = fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { Region: '', Area: '', 'Delivery-Date': '', 'Delivery-Time': '', 'Start-co': '', 'End-co': '' } })
    }).catch(function (e) {
      console.warn('[Delivery] cart attribute clear failed', e);
    }).then(function () { _cartWritePending = null; });
  }

  /* ---- Identity guard ------------------------------------------- */
  // Clear cached region when the customer identity changes, preventing
  // user X's region from leaking to user Y on the same browser.

  function guardIdentity() {
    // Build an identity key: logged-in users get their email/name,
    // guests get a fixed token so all guest sessions share one bucket.
    var identity;
    if (_config.isLoggedIn) {
      identity = 'user:' + (_config.customerFirstName || '') + ':' +
        (_config.customerRegion || '') + ':' + (_config.customerDistrict || '');
    } else {
      identity = 'guest';
    }

    try {
      var stored = localStorage.getItem(LS_CUSTOMER);
      if (stored && stored !== identity) {
        // Different user — clear cached delivery region
        localStorage.removeItem(LS_REGION);
        localStorage.removeItem(LS_DISTRICT);
        clearCartAttributes();
        try { sessionStorage.removeItem(SS_CART_SYNCED); } catch (_2) { /* */ }
        // Reset the once-per-session prompt guard so the new identity (e.g. a
        // guest after sign-out) is asked for their delivery area again.
        try { sessionStorage.removeItem(SS_PROMPTED); } catch (_3) { /* */ }
      }
      localStorage.setItem(LS_CUSTOMER, identity);
    } catch (_) { /* */ }

    // For logged-in users with metafields, seed localStorage from server
    // so the engine uses server truth on first load
    if (_config.isLoggedIn && _config.customerRegion) {
      try {
        if (!localStorage.getItem(LS_REGION)) {
          localStorage.setItem(LS_REGION, _config.customerRegion);
          if (_config.customerDistrict) {
            localStorage.setItem(LS_DISTRICT, _config.customerDistrict);
          }
        }
      } catch (_) { /* */ }
    }
  }

  /* ---- Auto-prompt for first-time logged-in users --------------- */

  function maybeAutoPrompt() {
    if (!_config) return;
    if (currentRegion()) return; // already has a region
    try {
      if (sessionStorage.getItem(SS_PROMPTED)) return; // already prompted this session
    } catch (_) { /* */ }

    setTimeout(function () {
      // Re-check in case region was set during the delay
      if (currentRegion()) return;
      try { sessionStorage.setItem(SS_PROMPTED, '1'); } catch (_) { /* */ }
      openSelector();
    }, PROMPT_DELAY);
  }

  /* ---- Public API ----------------------------------------------- */

  var API = {
    init: function (cfg) {
      _config  = cfg || {};
      guardIdentity();
      _loading = true;
      _promise = calc();

      _callbacks = [updateHeader];
      updateHeader(_promise);

      if (_config.slotsUrl) {
        fetchSlots(_config.slotsUrl, function (err, data) {
          if (data) _slotsRaw = normalize(data);
          _loading = false;
          tick();
        });
      } else {
        _loading = false;
      }

      if (_config.areasUrl) {
        fetchAreas(_config.areasUrl, function (err, data) {
          if (data) {
            _areasData = data.uae_areas || data;
            if (_selectorOpen) populateRegions();
          }
        });
      }

      // Sync region to cart attributes once per session
      var region = currentRegion();
      if (region) {
        try {
          if (!sessionStorage.getItem(SS_CART_SYNCED)) {
            syncCartAttributes(region, currentDistrict());
            sessionStorage.setItem(SS_CART_SYNCED, '1');
          }
        } catch (_) {
          syncCartAttributes(region, currentDistrict());
        }
      }

      clearInterval(_interval);
      _interval = setInterval(tick, TICK_MS);

      _ready = true;
      document.dispatchEvent(new CustomEvent('delivery:ready'));
    },

    getPromise: function () {
      return _promise || calc();
    },

    onUpdate: function (fn) {
      if (typeof fn !== 'function') return;
      _callbacks.push(fn);
      if (_promise) try { fn(_promise); } catch (_) { /* */ }
    },

    setRegion: function (region, district) {
      try {
        if (region) localStorage.setItem(LS_REGION, region);
        if (district) localStorage.setItem(LS_DISTRICT, district);
      } catch (_) { /* */ }

      syncCartAttributes(region, district);

      _promise = calc();
      fire();

      if (district) {
        var sel = document.querySelector('[data-region-selector]');
        var tpl = (sel && sel.dataset.i18nDeliveringTo) || 'Delivering to __DISTRICT__';
        toast(tpl.replace('__DISTRICT__', district));

        // Sign-in nudge removed — broke pill layout
      }
    },

    getRegions: function () {
      return _areasData || [];
    },

    getSlotSummary: function (name) {
      return slotSummary(name);
    },

    isReady: function () { return _ready && !_loading; },

    /** Await any pending cart attribute write (with timeout).
     *  Call before navigating to checkout. */
    awaitCartSync: function () {
      if (!_cartWritePending) return Promise.resolve();
      return Promise.race([
        _cartWritePending,
        new Promise(function (r) { setTimeout(r, CART_SYNC_TIMEOUT); })
      ]);
    },

    openSelector: openSelector,
    closeSelector: closeSelector
  };

  window.CasinettoDelivery = API;

  /* ---- Auto-init from header data attrs ------------------------- */

  function autoInit() {
    var hdr = document.querySelector('[data-delivery-engine]');
    if (!hdr) return;
    API.init({
      customerRegion:    hdr.dataset.customerRegion    || null,
      customerDistrict:  hdr.dataset.customerDistrict  || null,
      customerFirstName: hdr.dataset.customerFirstName || null,
      isLoggedIn:        hdr.dataset.customerLoggedIn === 'true',
      loginUrl:          hdr.dataset.loginUrl           || '/account/login',
      slotsUrl:          hdr.dataset.slotsUrl           || '',
      areasUrl:          hdr.dataset.areasUrl           || ''
    });

    bindSelectorEvents();
    maybeAutoPrompt();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
})();
